import React, { useState } from "react";
import { toast } from "sonner";
import { X, Zap } from "lucide-react";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";

const PACKAGES = [
  { key: "boost_24h", price: "€1.99", duration_key: "boost_24h_label" },
  { key: "boost_7d",  price: "€4.99", duration_key: "boost_7d_label" },
];

export default function BoostModal({ open, onClose, listing }) {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState("boost_24h");
  if (!open || !listing) return null;

  const checkout = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/boost/checkout", {
        listing_id: listing.id,
        lookup_key: selected,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("error_generic"));
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="boost-modal"
      className="fixed inset-0 z-[10006] flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-white rounded-2xl shadow-2xl">
        <div className="flex items-start justify-between px-6 py-4 border-b border-[#E6E2D8]">
          <div>
            <div className="overline text-[#B58A2F] flex items-center gap-1">
              <Zap size={12} strokeWidth={1.75} /> {t("boost")}
            </div>
            <h2 className="mt-1 font-serif-display text-xl text-[#143122]">
              {listing.title}
            </h2>
            <p className="mt-1 text-xs text-[#1A1F1C]/70">{t("boost_intro")}</p>
          </div>
          <button type="button" data-testid="boost-close" onClick={onClose} className="text-[#1A1F1C]/50 hover:text-[#143122]" aria-label="Close">
            <X size={18} strokeWidth={1.75} />
          </button>
        </div>

        <div className="p-6 space-y-3">
          {PACKAGES.map((p) => (
            <button
              key={p.key}
              type="button"
              data-testid={`boost-package-${p.key}`}
              onClick={() => setSelected(p.key)}
              className={`w-full text-left rounded-lg border-2 p-4 transition-colors ${
                selected === p.key ? "border-[#B58A2F] bg-[#B58A2F]/5" : "border-[#E6E2D8] hover:border-[#B58A2F]/50"
              }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-semibold text-[#143122]">{t(p.duration_key)}</div>
                  <div className="mt-1 text-xs text-[#1A1F1C]/70">{t(`${p.key}_desc`)}</div>
                </div>
                <div className="font-serif-display text-2xl text-[#B58A2F]">{p.price}</div>
              </div>
            </button>
          ))}
        </div>

        <div className="p-6 pt-0">
          <button
            type="button"
            data-testid="boost-checkout-btn"
            disabled={busy}
            onClick={checkout}
            className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-[#B58A2F] text-white hover:bg-[#8f6d20] disabled:opacity-50 px-4 py-2.5 text-sm font-medium"
          >
            <Zap size={14} strokeWidth={1.75} /> {busy ? "..." : t("proceed_to_payment")}
          </button>
          <p className="mt-3 text-[10px] text-[#1A1F1C]/50 text-center">
            {t("stripe_powered")}
          </p>
        </div>
      </div>
    </div>
  );
}
